/* Mg Theme — shared helpers used across all components. (c) 2026 NAMY SOLUTIONS LLC */

window.Mg = window.Mg || {};

/* formatMoney — single source of truth for currency formatting */

Mg.formatMoney = (function() {
  /* Mirrors the Liquid `| money` filter from the store's money_format, never the page locale. */
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var rawFormat = null;
  var decodedFormat = null;

  function decodeEntities(str) {
    if (str.indexOf('&') === -1) return str; // money_format may carry &euro; &nbsp; etc.
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  }

  function plainFormat(str) {
    // The store may use the "HTML with currency" money_format
    // (e.g. <span class="money">{{amount}}</span> €). Mg paints prices via
    // textContent, so strip tags to plain text BEFORE decoding entities —
    // otherwise the raw <span> would render as literal text.
    return decodeEntities(str.replace(/<[^>]*>/g, '')).trim();
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    if (precision == null) precision = 2;
    if (thousands == null) thousands = ',';
    if (decimal == null) decimal = '.';
    if (isNaN(cents) || cents == null) cents = 0;
    var parts = (cents / 100).toFixed(precision).split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  return function(cents) {
    cents = Math.round(Number(cents) || 0);
    var format = (window.Shopify && window.Shopify.money_format) || null;

    if (format) {
      if (format !== rawFormat) { rawFormat = format; decodedFormat = plainFormat(format); }
      var match = decodedFormat.match(placeholderRegex);
      if (match) {
        var value;
        switch (match[1]) {
          case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
          case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
          case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
          case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
          case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
          case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
          default: value = withDelimiters(cents, 2); break; // 'amount'
        }
        return decodedFormat.replace(placeholderRegex, value);
      }
    }

    /* Last-resort fallback when no money_format is exposed (non-Mg host). */
    if (window.Shopify && window.Shopify.formatMoney) return window.Shopify.formatMoney(cents);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
    }).format(cents / 100);
  };
})();

/* debug — console.error gated behind Shopify.designMode */

Mg.debug = function(context, error) {
  if (window.Shopify && window.Shopify.designMode) console.error('[Mg:' + context + ']', error);
};

/* Runs fn now, or once a prerendered page is activated. */

Mg.runWhenActive = function(fn) {
  if (document.prerendering) {
    document.addEventListener('prerenderingchange', fn, { once: true });
  } else {
    fn();
  }
};

/* debounce — reusable timer wrapper */

Mg.debounce = function(fn, delay) {
  var timer;
  var debounced = function() {
    var args = arguments;
    var self = this;
    clearTimeout(timer);
    timer = setTimeout(function() {
      fn.apply(self, args);
    }, delay);
  };
  debounced.cancel = function() { clearTimeout(timer); };
  return debounced;
};

/* Escapes text for HTML content and attribute values. */
Mg.escapeHtml = function(str) {
  return str == null ? '' : String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
};

/* Restart a one-shot CSS keyframe by re-applying its class (remove → reflow → add). Keyframes must be one-shot (no `infinite`/`forwards`) so the lingering class is inert. */
Mg.replayAnimation = Mg.replayAnimation || function(el, className) {
  if (!el) return;
  el.classList.remove(className);
  void el.offsetWidth; // force reflow so the keyframe restarts
  el.classList.add(className);
};

/* Horizontal swipe between two touch points, in el's writing direction: 1 next, -1 previous, 0 none. */
Mg.swipeStep = function(el, from, to) {
  var dx = to.clientX - from.clientX, dy = to.clientY - from.clientY;
  if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  var step = dx < 0 ? 1 : -1;
  return getComputedStyle(el).direction === 'rtl' ? -step : step;
};

/* Cart API — centralized fetch wrappers */

Mg.cart = {
  /* One FIFO queue for every cart read and write: concurrent /cart/*.js calls race server-side, and a late `sections` snapshot would paint a cart that a sibling call has already changed. */
  _chain: Promise.resolve(),
  _enqueue: function(task) {
    var result = Mg.cart._chain.then(function() { return task(); });
    /* A failed op never stalls the queue; the caller's promise still rejects. */
    Mg.cart._chain = result.then(function() {}, function() {});
    return result;
  },

  /* Markets-safe: Shopify.routes.root carries the locale sub-folder and ends with a slash, so `path` has no leading slash. Read at call time. */
  _url: function(path) {
    return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + path;
  },

  /* Start times (performance.now) of the theme's own cart mutations, so the network watcher below can tell them apart from a third-party app's. */
  _own: [],

  _fetch: function(url, body, sections) {
    var payload = body || {};
    Mg.cart._own.push(performance.now());
    if (Mg.cart._own.length > 20) Mg.cart._own.shift();
    if (sections && sections.length)  payload.sections = sections.join(',');
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function(response) {
      if (!response.ok) {
        /* Catch only the JSON parse, so Shopify's own message reaches the shopper. */
        return response.json().catch(function() {
          return null;
        }).then(function(data) {
          var message = (data && (data.description || data.message)) ||
                        ('Cart request failed: ' + response.status);
          throw new Error(message);
        });
      }
      return response.json();
    });
  },

  /* Every mutation announces the new cart once, from here. `opts.source` names the originator; `opts.announce === false` lets a batch announce at the end. */
  _mutate: function(path, body, sections, opts, returnsCart) {
    return Mg.cart._enqueue(function() {
      return Mg.cart._fetch(Mg.cart._url(path), body, sections);
    }).then(function(data) {
      Mg.cart._announce(data, opts, returnsCart);
      return data;
    });
  },

  _announce: function(data, opts, returnsCart) {
    if (opts && opts.announce === false) return;
    var source = opts && opts.source;
    if (returnsCart) {
      var cart = Object.assign({}, data);
      delete cart.sections;
      Mg.cart.notify(cart, source);
      return;
    }
    /* /cart/add.js returns the added lines only: read the whole cart, queued behind the add. */
    Mg.cart.get()
      .then(function(c) { Mg.cart.notify(c, source); })
      .catch(function(e) { Mg.debug('Cart:announce', e); });
  },

  add: function(items, sections, opts) { return Mg.cart._mutate('cart/add.js', { items: items }, sections, opts, false); },

  change: function(id, quantity, sections, opts) {
    return Mg.cart._mutate('cart/change.js', { id: id, quantity: quantity }, sections, opts, true);
  },

  update: function(body, sections, opts) { return Mg.cart._mutate('cart/update.js', body, sections, opts, true); },

  get: function() {
    return Mg.cart._enqueue(function() {
      return fetch(Mg.cart._url('cart.js'), { headers: { 'Accept': 'application/json' } })
        .then(function(r) { return r.ok ? r.json() : null; });
    });
  },

  /** Remove the cart lines whose property propName equals propValue ('true' by default). */
  removeLinesWithProperty: function(propName, propValue) {
    if (!propName) return Promise.resolve({ removed: 0 });
    var expected = propValue == null ? 'true' : propValue;
    return Mg.cart.get().then(function(cart) {
      if (!cart || !cart.items) return { removed: 0 };
      var keys = cart.items
        .filter(function(item) {
          return item.properties && item.properties[propName] === expected;
        })
        .map(function(item) { return item.key; });
      if (!keys.length) return { removed: 0 };
      return Promise.all(keys.map(function(key) {
        return Mg.cart.change(key, 0).catch(function(e) {
          Mg.debug('Cart:removeLinesWithProperty', e);
        });
      })).then(function() {
        return { removed: keys.length };
      });
    });
  },

  /* One /cart/update.js call for every code (each call replaces the last), then re-read the cart to split applied from rejected. `owned`: see reconcileOfferCodes. */
  applyPendingCodes: function(codes, owned) {
    if (!Array.isArray(codes)) codes = [];
    var flat = codes
      .filter(function(c) { return typeof c === 'string'; })
      .map(function(c) { return c.trim(); })
      .filter(function(c) { return c.length > 0; });
    if (!flat.length) return Promise.resolve({ applied: [], rejected: [] });

    /* Case-insensitive dedupe preserving first-occurrence order */
    var seen = {};
    flat = flat.filter(function(c) {
      var k = c.toLowerCase();
      if (seen[k]) return false;
      seen[k] = true;
      return true;
    });

    var csv = flat.join(',');
    var body = { discount: csv };
    if (owned && typeof owned === 'object') {
      /* Same request as the discount — no extra round-trip, and the two can never disagree because they land together. */
      body.attributes = {};
      body.attributes[Mg.cart.OWNED_ATTR] = Object.keys(owned).length
        ? JSON.stringify(owned)
        : '';
    }
    return Mg.cart.update(body)
      .then(Mg.cart.get)
      .then(function(cart) {
        var appliedSet = {};
        ((cart && cart.discount_codes) || []).forEach(function(d) {
          if (d.applicable) appliedSet[d.code.toLowerCase()] = d.code;
        });
        var applied = [];
        var rejected = [];
        flat.forEach(function(c) {
          var match = appliedSet[c.toLowerCase()];
          if (match) applied.push(match);
          else rejected.push(c);
        });
        if (rejected.length)  Mg.debug('Cart:codesRejected', rejected.join(', '));
        return { applied: applied, rejected: rejected };
      })
      .catch(function(e) {
        Mg.debug('Cart:applyPendingCodes', e);
        /* `failed`: the request never landed, as opposed to codes Shopify refused. */
        return { applied: [], rejected: flat, failed: true };
      });
  },

  /* The single announcement of a cart change, on the bus and the document, with the full cart and its `_source` so a listener can skip its own echo. */
  notify: function(cart, source) {
    if (!cart) return;
    var detail = Object.assign({}, cart);
    if (source) detail._source = source;
    if (window.MgEvents) window.MgEvents.publish(window.MgEvents.Events.CART_UPDATED, detail);
    document.dispatchEvent(new CustomEvent('namy:cart:updated', { detail: detail }));
  },

  /* Apply in one call the pending codes of every offer in scope (`getPendingCodes()` or `[data-mg-pending-codes]`); `justifiedIds` are the variants that justify them. */
  applyAllPendingCodes: function(scope, justifiedIds) {
    var root = scope || document;
    var sel = 'mg-quantity-breaks, mg-subscription-toggle, mg-cross-sell-card, [data-mg-pending-codes]';

    /* Harvest the codes of the offers ACTIVE in scope (selected tier, etc.). */
    var harvest = [];
    root.querySelectorAll(sel).forEach(function(el) {
      if (typeof el.getPendingCodes === 'function') {
        try { harvest = harvest.concat(el.getPendingCodes() || []); }
        catch (e) { Mg.debug('Cart:applyAllPendingCodes', e); }
      }
    });

    /* Nothing active to apply: keep every code already on the cart. A tier without its own code relies on an automatic discount or an earlier code. */
    if (!harvest.length) return Promise.resolve({ applied: [], rejected: [], mutated: false });

    /* Every code the theme defines: tells an offer code from a typed one. */
    var knownOffer = {};
    document.querySelectorAll('[data-promo-code]').forEach(function(el) {
      var c = (el.getAttribute('data-promo-code') || '').trim();
      if (c) knownOffer[c.toLowerCase()] = true;
    });

    /* Offers are mutually exclusive: the active one replaces other offer codes, typed codes stay. update({discount}) replaces the whole set. */
    return Mg.cart.get().then(function(cart) {
      var applied = ((cart && cart.discount_codes) || [])
        .filter(function(d) { return d.applicable; })
        .map(function(d) { return d.code; });
      var manual = applied.filter(function(c) { return !knownOffer[c.toLowerCase()]; });
      var desired = manual.concat(harvest);

      /* Lines behind each offer code, kept as a cart attribute. Single underscore: a `__name` attribute is not stored. */
      var prior = Mg.cart._readOwned(cart);
      var owned = {};
      /* Keep ownership only for codes still on the cart, or a code later typed by hand would be treated as an offer's and removed with it. */
      desired.forEach(function(code) {
        var key = String(code).toLowerCase();
        if (prior[key]) owned[key] = prior[key];
      });
      harvest.forEach(function(code) {
        var key = String(code).toLowerCase();
        var ids = (justifiedIds || []).filter(function(n) { return n; });
        owned[key] = ids.length ? ids : (owned[key] || []);
      });
      return Mg.cart.applyPendingCodes(desired, owned);
    }).then(function(res) {
      /* The cart changed after any snapshot taken before the apply: callers render the drawer afresh. */
      return { applied: res.applied, rejected: res.rejected, mutated: true };
    });
  },

  /** Ownership map stored on the cart: { "code": [variantId, …] }. */
  OWNED_ATTR: '_namy_offer_codes',

  _readOwned: function(cart) {
    try {
      var raw = cart && cart.attributes && cart.attributes[Mg.cart.OWNED_ATTR];
      var parsed = raw ? JSON.parse(raw) : {};
      return (parsed && typeof parsed === 'object') ? parsed : {};
    } catch (e) {
      /* An unreadable map means nothing is tracked. */
      Mg.debug('Cart:ownedParse', e);
      return {};
    }
  },

  /* Drop every offer code whose justifying lines have left the cart: ownership, not `applicable`, decides. Hand-typed codes are never owned. */
  reconcileOfferCodes: function(sections) {
    return Mg.cart.get().then(function(cart) {
      var codes = (cart && cart.discount_codes) || [];
      if (!codes.length) return { changed: false, removed: [], data: null };

      var owned = Mg.cart._readOwned(cart);
      var live = {};
      ((cart && cart.items) || []).forEach(function(it) { live[it.variant_id] = true; });
      var empty = !((cart && cart.item_count) > 0);

      var removed = [];
      var keep = codes.filter(function(d) {
        var ids = owned[String(d.code).toLowerCase()];
        var isOurs = !!ids;
        var justified = isOurs && !empty && ids.some(function(id) { return live[id]; });
        /* Ours and unjustified -> drop. Not ours (hand-typed) -> keep unless the cart is empty. */
        var stays = isOurs ? justified : !empty;
        if (!stays) removed.push(d.code);
        return stays;
      }).map(function(d) { return d.code; });

      if (!removed.length) return { changed: false, removed: [], data: null };

      var nextOwned = {};
      Object.keys(owned).forEach(function(k) { if (keep.some(function(c) { return c.toLowerCase() === k; })) nextOwned[k] = owned[k]; });
      var body = { discount: keep.join(',') };
      body.attributes = {};
      body.attributes[Mg.cart.OWNED_ATTR] = Object.keys(nextOwned).length ? JSON.stringify(nextOwned) : '';
      return Mg.cart.update(body, sections).then(function(data) {
        Mg.debug('Cart:offerCodesDropped', removed.join(', '));
        return { changed: true, removed: removed, data: data };
      });
    });
  }
};

/* Gift price guard: gift lines (`_gift`, `_auto_gift`) cost nothing only while a discount applies; the cart is read back. */
Mg.gifts = {
  FALLBACK: {
    replaced: 'Your gift is no longer free: a promotion already applied to your cart takes priority. Your total is still the lowest available.',
    billed: 'This gift is currently billed at full price.',
    unavailable: 'This gift is momentarily unavailable.'
  },

  isGiftLine: function(item) {
    var p = (item && item.properties) || {};
    return p._gift === 'true' || p._auto_gift === 'true';
  },

  notice: function(message) {
    if (!message) return;
    /* Anchored outside what the drawer rewrites on render; the drawer is on every page. */
    var host = document.querySelector('[data-cart-drawer-body]') ||
               document.querySelector('[data-cart-items]');
    if (!host) return;
    var el = host.querySelector('[data-mg-gift-notice]');
    if (!el) {
      el = document.createElement('p');
      el.setAttribute('data-mg-gift-notice', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.className = 'mg-gift-notice';
      host.insertBefore(el, host.firstChild);
    }
    el.textContent = message;
  },

  clearNotice: function() {
    var el = document.querySelector('[data-mg-gift-notice]');
    if (el) el.remove();
  },

  /* Translated strings are read from the document; absent → English fallback. */
  labels: function() {
    var el = document.querySelector('[data-gift-notice-replaced]');
    return el ? {
      replaced: el.getAttribute('data-gift-notice-replaced'),
      billed: el.getAttribute('data-gift-notice-billed'),
      unavailable: el.getAttribute('data-gift-notice-unavailable')
    } : {};
  },

  verify: function(cart, labels) {
    if (!cart) return;
    var texts = labels || Mg.gifts.labels();
    var billed = (cart.items || []).filter(function(i) {
      return Mg.gifts.isGiftLine(i) && i.final_line_price > 0;
    });
    if (!billed.length) { Mg.gifts.clearNotice(); return; }
    /* A discount took priority, or none was configured: claim the first only when the cart carries one. */
    var discounted = cart.total_discount > 0 || (cart.cart_level_discount_applications || []).length > 0;
    var key = discounted ? 'replaced' : 'billed';
    Mg.debug('Gifts:billed', billed.map(function(i) { return i.variant_id; }).join(', '));
    Mg.gifts.notice(texts[key] || Mg.gifts.FALLBACK[key]);
  }
};

/* Splide helpers shared by the carousel components. */

Mg.splide = {
  // Mobile peek = % of next slide visible on the right edge.
  // Signals swipe affordance on touch devices (Baymard mobile carousel pattern).
  // Applied via Splide track `padding` option, only at perPage:1 breakpoint.
  PEEK_MAP: { none: '0', small: '8%', medium: '15%', large: '22%' },

  resolvePeek: function(value) { return this.PEEK_MAP[value] || this.PEEK_MAP.none; }
};

/* Section Renderer — parse HTML + swap selectors */

Mg.section = {
  fetch: function(url, sectionId, signal) {
    var sep = url.indexOf('?') >= 0 ? '&' : '?';
    return fetch(url + sep + 'section_id=' + sectionId, signal ? { signal: signal } : undefined)
      .then(function(r) { return r.ok ? r.text() : ''; });
  },

  render: function(html, swaps) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    swaps.forEach(function(swap) {
      var incoming = doc.querySelector(swap.selector);
      var current = document.querySelector(swap.selector);
      if (incoming && current) {
        if (swap.mode === 'text') {
          current.textContent = incoming.textContent;
        } else {
          current.innerHTML = incoming.innerHTML;
        }
      }
    });
  }
};

/* Apps can change the cart without telling the theme. PerformanceObserver sees every request without patching fetch; only MUTATIONS count, never reads, or the refetch would observe itself. */
window.__namyCartWatchInit = window.__namyCartWatchInit || (function () {
  if (typeof PerformanceObserver === 'undefined') return false;

  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  /* A request started by Mg.cart is already being painted by its caller. */
  function isOwn(entry) {
    var own = Mg.cart._own;
    for (var i = 0; i < own.length; i++) {
      if (Math.abs(entry.startTime - own[i]) < 50) { own.splice(i, 1); return true; }
    }
    return false;
  }

  var announceSoon = Mg.debounce(function () { announce(); }, 250);

  function announce() {
    Mg.cart.get()
      .then(function (cart) { Mg.cart.notify(cart, 'external-app'); })
      .catch(function (err) { Mg.debug('CartWatch', err); });
  }

  try {
    new PerformanceObserver(function (list) {
      var hit = list.getEntries().some(function (entry) { return MUTATION.test(entry.name) && !isOwn(entry); });
      if (!hit) return;
      /* Several lines land as separate requests; coalesce into one refetch. */
      announceSoon();
    }).observe({ type: 'resource', buffered: true });
  } catch (err) {
    Mg.debug('CartWatch:observe', err);
    return false;
  }

  return true;
})();

/* Shopify's standard cart actions (apps, WebMCP) bypass the watch above: announce them. */
window.__namyCartActionsInit = window.__namyCartActionsInit || (function () {
  var done = false;

  function configure() {
    var actions = window.Shopify && window.Shopify.actions;
    if (done || !actions || !actions.updateCart || typeof actions.updateCart.configure !== 'function') return done;
    done = true;

    actions.updateCart.configure({
      /* The drawer already listens for the standard events on the document. */
      eventTarget: function () { return null; },
      handler: function (defaultHandler) {
        return defaultHandler().then(function (result) {
          if (result && result.userErrors && result.userErrors.length) return result;
          Mg.cart.get()
            .then(function (cart) { Mg.cart.notify(cart, 'standard-action'); })
            .catch(function (err) { Mg.debug('CartActions', err); });
          return result;
        });
      }
    });

    if (actions.openCart && typeof actions.openCart.configure === 'function') {
      actions.openCart.configure({
        handler: function () {
          /* The drawer element only renders when the merchant chose it. */
          var drawer = document.querySelector('mg-cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
          else window.location.assign(Mg.cart._url('cart'));
        }
      });
    }
    return true;
  }

  /* Shopify's actions module can run after this file: retry on the next load events. */
  if (!configure()) {
    document.addEventListener('DOMContentLoaded', configure);
    if (window.addEventListener) window.addEventListener('load', configure);
  }
  return true;
})();

/* Marks a horizontal scroller with the side that continues (data-overflowing: end, start or both) for its edge fade. Returns a cleanup. */
Mg.watchOverflow = function(bar, isVertical) {
  var sync = function() {
    var slack = bar.scrollWidth - bar.clientWidth;
    if ((isVertical && isVertical()) || slack <= 1) { bar.removeAttribute('data-overflowing'); return; }
    var pos = Math.abs(bar.scrollLeft); /* negative in right-to-left writing */
    bar.setAttribute('data-overflowing', pos <= 1 ? 'end' : pos >= slack - 1 ? 'start' : 'both');
  };
  sync();
  bar.addEventListener('scroll', sync, { passive: true });
  var ro = window.ResizeObserver ? new ResizeObserver(sync) : null;
  if (ro) ro.observe(bar);
  /* Labels widen when the web font swaps in, while the row keeps its width. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function() { if (bar.isConnected) sync(); });
  return function() { bar.removeEventListener('scroll', sync); if (ro) ro.disconnect(); };
};
/* A row outside any component opts in with data-overflow-watch="auto"; its current item starts centred. Rows swapped in later are bound by the caller. */
Mg.bindOverflowRows = function(root) { (root || document).querySelectorAll('[data-overflow-watch="auto"]').forEach(function(bar) { if (bar.namyWatched) return; bar.namyWatched = true; var cur = bar.querySelector('[aria-current="page"]'); if (cur) bar.scrollLeft += cur.getBoundingClientRect().left - bar.getBoundingClientRect().left - (bar.clientWidth - cur.offsetWidth) / 2; Mg.watchOverflow(bar); }); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function() { Mg.bindOverflowRows(); }); else Mg.bindOverflowRows();

/* Card → product image morph: only the clicked card gets the shared name (duplicate names break WebKit). */
(function () {
  if (window.__namyCardMorphInit || !('onpageswap' in window)) return;
  window.__namyCardMorphInit = true;
  window.addEventListener('pageswap', function (e) {
    document.querySelectorAll('.is-vt-source').forEach(function (el) { el.classList.remove('is-vt-source'); });
    var entry = e.viewTransition && e.activation && e.activation.entry;
    if (!entry) return;
    var path = new URL(entry.url).pathname;
    if (path.indexOf('/products/') === -1) return;
    var links = document.querySelectorAll('.mg-product-card a[href]');
    for (var i = 0; i < links.length; i++) {
      if (new URL(links[i].href, location.href).pathname !== path) continue;
      var media = links[i].closest('.mg-product-card').querySelector('.mg-product-card__media');
      if (media) media.classList.add('is-vt-source');
      return;
    }
  });
})();

/* Fake-variant tap: mark the choice, show loading; undone on bfcache restore. */
(function () {
  if (window.__namyFakeVariantNavInit || !window.addEventListener) return;
  window.__namyFakeVariantNavInit = true;
  var previous = null, chosen = null, root = document.documentElement, current = 'mg-fake-variants__option--current';
  function mark(el, on) { if (!el) return; el.classList.toggle(current, on); if (on) el.setAttribute('aria-current', 'page'); else el.removeAttribute('aria-current'); }
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('a.mg-fake-variants__option[href]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || link.target === '_blank') return;
    mark(previous = link.closest('.mg-fake-variants__options').querySelector('[aria-current="page"]'), false);
    mark(chosen = link, true);
    root.setAttribute('data-navigating', '');
  });
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    root.removeAttribute('data-navigating');
    mark(chosen, false);
    mark(previous, true);
    previous = chosen = null;
  });
})();
